import React, { Suspense, useEffect } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router';
import { AuthCurrentLoader } from 'client/components/auth/current-loader/AuthCurrentLoader';
import { NotFoundPage } from 'client/components/errors/not-found/NotFoundPage';
import { useModalHistoryCleanup } from 'client/ui/modal/useModalHistoryCleanup';
import { PrivateRoute } from 'client/components/auth/routes/PrivateRoute';
import { AnonymousRoute } from 'client/components/auth/routes/AnonymousRoute';
import { ProceduresListViewPage } from './front/procedures/list/ProceduresListViewPage';
import { ProcedureViewPage } from './front/procedures/single/ProcedureViewPage';
import { ProcedureFormPage } from './front/procedures/single/ProcedureFormPage';
import { RequestsListViewPage } from './front/request/list/RequestsListViewPage';
import { RequestSentPage } from './front/request/sent/RequestSentPage';
import { RequestFormPage } from './front/request/form/RequestFormPage';
import { RequestDossierPage } from './front/request/dossier/RequestDossierPage';
import { RequestMainDocumentPage } from './front/request/dossier/document/RequestMainDocument';
import { RequestDocumentPage } from './front/request/dossier/document/RequestDocumentPage';
import { UserRegistrationPage } from './front/users/UserRegistrationPage';
import { UserFrontLoginPage } from './front/users/UserFrontLoginPage';
import { UserActivationPage } from './front/users/UserActivationPage';
import { UserForgotPasswordPage } from './front/users/UserForgotPasswordPage';
import { UserResetPasswordPage } from './front/users/UserResetPasswordPage';
import { RoutePaths } from 'common/routes/RoutePaths';
import {
  useHasFeature,
  useModuleConfigOptions,
  useModulesConfig,
  useSecurityStrategyConfig,
  useThemeConfig
} from 'client/components/schema/config/ClientConfigModule';
import { Helmet } from 'react-helmet';
import i18next from 'i18next';
import { getIntlDefaultLanguage } from 'client/core/intl/IntlLanguages';
import loadable from '@loadable/component';
import { LoaderSpin } from 'client/ui/loader/LoaderSpin';
import { UserEmailValidationPage } from './front/users/UserEmailValidationPage';
import { UserProfilePage } from './front/users/UserProfilePage';
import { EmployeeFrontLoginPage } from '../components/auth/login/soap-ldap/EmployeeFrontLoginPage';
import { NationalDataPlatformVerificationPage } from './front/integrations/pdnd/NationalDataPlatformVerificationPage';

/**
 * Lazy loading della sezione di amministrazione
 */
const AdminRoute = loadable(
  () => import(/* webpackChunkName: "admin" */ './admin/AdminRoute'),
  { fallback: <LoaderSpin full /> }
);

const defaultLang = getIntlDefaultLanguage();

export const Routes = () => {
  const hasCitizenRegistration = useHasFeature('citizen.registration');
  const hasSoapLdapStrategy = useSecurityStrategyConfig('soap-ldap');
  const { labels } = useThemeConfig();

  // Chiudiamo tutte le modal alla navigazione
  useModalHistoryCleanup();

  return (
    <>
      <Helmet
        titleTemplate={`%s | ${labels.pageTitle}`}
        htmlAttributes={{
          lang: i18next.language
        }}
      >
        <title>{labels.portalName}</title>
        {labels.pageMetaDescription && (
          <meta name="description" content={labels.pageMetaDescription} />
        )}
      </Helmet>
      <AuthCurrentLoader>
        <Switch>
          <Route exact path="/" component={ProceduresListViewPage} />
          <Route path="/domande/:id/modulo" component={ProcedureFormPage} />
          <Route path="/domande/:id" component={ProcedureViewPage} />
          <PrivateRoute
            path="/richieste/:id/dettaglio/documento/:token"
            permission={['request.read:own', 'request.read']}
            component={RequestDocumentPage}
          />
          <PrivateRoute
            path="/richieste/:id/dettaglio/documento"
            permission="request.read:own"
            component={RequestMainDocumentPage}
          />
          {/* TODO verifica pagine da visualizzare per richieste anonime */}
          <PrivateRoute
            path="/richieste/:id/dettaglio/fascicolo"
            permission="request.read:own"
            component={RequestDossierPage}
          />
          <PrivateRoute
            path="/richieste/:id/modulo"
            permission="request.read:own"
            component={RequestFormPage}
          />
          <PrivateRoute
            path="/richieste/:id/dettaglio"
            permission="request.read:own"
            component={RequestSentPage}
          />
          <PrivateRoute
            path="/richieste"
            permission="request.read:own"
            component={RequestsListViewPage}
          />

          {hasSoapLdapStrategy && (
            <AnonymousRoute
              key="/utente/login/dipendenti"
              path={RoutePaths['/utente/login/dipendenti']}
              component={EmployeeFrontLoginPage}
            />
          )}

          <PrivateRoute
            key="/pdnd/accertamenti-c020"
            path={RoutePaths['/pdnd/accertamenti-c020']}
            permission="pdnd.c020-verify"
            component={NationalDataPlatformVerificationPage}
          />

          {/** Registrazione e Login cittadini */}
          {hasCitizenRegistration && [
            <AnonymousRoute
              key="/utente/registrazione"
              path="/utente/registrazione"
              component={UserRegistrationPage}
            />,
            <PrivateRoute
              key="/utente/profilo"
              path="/utente/profilo"
              permission="current-user.edit"
              component={UserProfilePage}
            />,
            <AnonymousRoute
              key="/utente/attivazione"
              path={RoutePaths['/utente/attivazione']}
              component={UserActivationPage}
            />,
            <AnonymousRoute
              key="/utente/forgot"
              path="/utente/forgot"
              component={UserForgotPasswordPage}
            />,
            <AnonymousRoute
              key="/utente/reimposta-password"
              path={RoutePaths['/utente/reimposta-password']}
              component={UserResetPasswordPage}
            />,
            <AnonymousRoute
              key="/utente/login"
              path={RoutePaths['/utente/login']}
              component={UserFrontLoginPage}
            />,
            <AnonymousRoute
              key="/utente/completa-profilo"
              path={RoutePaths['/utente/completa-profilo']}
              component={UserEmailValidationPage}
            />
          ]}

          {/** Amministrazione */}
          <Route path="/admin" component={AdminRoute} />

          <Route component={NotFoundPage} />
        </Switch>
      </AuthCurrentLoader>
    </>
  );
};
